import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { SplitRuns } from '@/components/generals/split-runs'
import { EnquiryForm } from '@/components/generals/enquiry-form'
import { AvailabilityMeter } from '@/components/contact/availability-meter'

import { media } from '@/constants/media'
import { studioMeta } from '@/constants/site'
import { contactSteps } from '@/constants/studio'

/* A flat ink panel, not a photograph behind a 65% scrim. The composition
   carries it instead: a ruled capacity rail across the top, the statement,
   then the form set as a sentence with the brief rail beside it. */
export const ContactPanel: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useGsap(() => {
        const root = scope.current

        if (!root || prefersReducedMotion()) {
            return
        }

        gsap.fromTo(root.querySelectorAll('[data-panel-rule]'),
            { scaleX: 0 },
            {
                scaleX: 1,
                duration: 1.2,
                stagger: 0.12,
                transformOrigin: 'left',
                ease: 'noma',
                scrollTrigger: { trigger: root, start: 'top 80%', once: true }
            }
        )

        gsap.fromTo(root.querySelectorAll('[data-panel-head]'),
            { opacity: 0, y: 24 },
            {
                opacity: 1,
                y: 0,
                duration: 0.95,
                stagger: 0.09,
                ease: 'noma',
                scrollTrigger: { trigger: root, start: 'top 78%', once: true }
            }
        )

        const stepGrid = root.querySelector('[data-step-grid]')

        if (stepGrid) {
            gsap.fromTo(root.querySelectorAll('[data-step-row]'),
                { opacity: 0, x: -18 },
                {
                    opacity: 1,
                    x: 0,
                    duration: 0.9,
                    stagger: 0.1,
                    ease: 'noma',
                    scrollTrigger: { trigger: stepGrid, start: 'top 90%', once: true }
                }
            )
        }
    }, scope, [])

    return (
        <section
            id='contact-form'
            ref={scope}
            className='relative bg-paper px-5 sec-b text-ink lg:px-[33px]'
        >
            <div className='mx-auto max-w-[1920px]'>
                <div className='overflow-hidden rounded-[var(--radius-frame)] bg-ink-soft text-paper'>
                    <div className='flex flex-col gap-10 px-5 py-11 lg:gap-16 lg:px-[50px] lg:py-[66px]'>
                        {/* Capacity rail */}
                        <div className='flex flex-col gap-5'>
                            <span data-panel-rule className='block h-px w-full origin-left bg-paper/20' />

                            <div className='flex flex-wrap items-center justify-between gap-6'>
                                <span data-panel-head className='t-mono text-ash-soft'>
                                    Book a call
                                </span>

                                <div data-panel-head>
                                    <AvailabilityMeter />
                                </div>

                                <div data-panel-head className='flex items-center gap-3'>
                                    <img
                                        src={media.people.founderAvatar}
                                        alt=''
                                        aria-hidden='true'
                                        loading='lazy'
                                        className='size-10 shrink-0 rounded-full object-cover'
                                    />

                                    <span className='t-cond-soft flex flex-col text-[13px] leading-[1.3]'>
                                        <span className='text-paper'>
                                            Jin Yuen
                                        </span>

                                        <span className='text-ash-soft'>
                                            Founder
                                        </span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Statement */}
                        <div className='flex flex-col gap-8'>
                            <SplitRuns
                                className='t-display max-w-[18ch] text-[clamp(38px,6.4vw,92px)] leading-[0.92] text-paper'
                                runs={[
                                    { text: "Let's make you " },
                                    { text: 'impossible', className: 'text-brand' },
                                    { text: ' to ignore.' }
                                ]}
                            />

                            <SplitRuns
                                as='p'
                                className='t-cond max-w-[52ch] text-[clamp(17px,1.6vw,23px)] leading-[1.16]'
                                runs={[
                                    { text: "Tell us what you're building. ", className: 'text-ash-soft' },
                                    { text: "We'll tell you how we'd make it land.", className: 'text-paper' }
                                ]}
                            />
                        </div>

                        {/* Form plus brief rail */}
                        <div className='grid gap-12 lg:grid-cols-[1fr_0.42fr] lg:gap-16'>
                            <EnquiryForm />

                            <div data-step-grid className='flex flex-col gap-8'>
                                <div className='flex flex-col gap-5'>
                                    <span data-panel-rule className='block h-px w-full origin-left bg-paper/20' />

                                    <span className='t-mono text-ash-soft'>
                                        What happens next
                                    </span>
                                </div>

                                <ol className='flex flex-col gap-7'>
                                    {contactSteps.map((step) => (
                                        <li key={step.index} data-step-row className='flex gap-4'>
                                            <span className='t-mono shrink-0 pt-[0.2em] text-brand'>
                                                {step.index}
                                            </span>

                                            <span className='flex flex-col gap-1.5'>
                                                <span className='t-cond-soft text-[15px] leading-[1.3] text-paper'>
                                                    {step.title}
                                                </span>

                                                <span className='t-cond-soft text-[13px] leading-[1.55] text-ash-soft'>
                                                    {step.copy}
                                                </span>
                                            </span>
                                        </li>
                                    ))}
                                </ol>

                                <div className='flex flex-col gap-3 pt-2'>
                                    <span data-panel-rule className='block h-px w-full origin-left bg-paper/20' />

                                    <a
                                        href={`tel:${studioMeta.phoneTel}`}
                                        data-cursor='link'
                                        className='t-cond pt-3 text-[clamp(19px,2.2vw,26px)] leading-[1.1] text-paper transition-colors duration-300 hover:text-brand'
                                    >
                                        {studioMeta.phone}
                                    </a>

                                    <a
                                        href={`mailto:${studioMeta.email}`}
                                        data-cursor='link'
                                        className='t-cond-soft text-[15px] text-ash-soft transition-colors duration-300 hover:text-brand'
                                    >
                                        {studioMeta.email}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
